import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Truck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { ShipmentForm } from "@/components/forms/ShipmentForm";
import BulkDeleteButton from "@/components/BulkDeleteButton";
import { SearchFilters } from "@/components/SearchFilters";
import { Pagination } from "@/components/Pagination";
import { TableSkeleton } from "@/components/Skeleton";
import { SHIPMENT_STATUSES } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 25;

export default function ShipmentsListPage() {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [clients, setClients] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const page = Math.max(1, parseInt(params.get("page") || "1"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        q: params.get("q") || undefined,
        status: params.get("status") || undefined,
        client: params.get("client") || undefined,
        page, page_size: PAGE_SIZE,
      };
      if (isStaff) {
        api.get("/products", { params: { status_in: "In Warehouse,Ready to Ship", page_size: 500 } })
          .then((p) => setAvailableProducts(p.data.items || []))
          .catch(() => setAvailableProducts([]));
      }
      const [s, c] = await Promise.all([
        api.get("/shipments", { params: q }),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
      ]);
      setData(s.data);
      setClients((c.data || []).filter((x) => !x.is_archived));
      setSelectedIds([]);
    } finally { setLoading(false); }
  }, [params, page, isStaff]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allIds = data.items.map((s) => s.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);

  const colCount = (isAdmin ? 1 : 0) + (isStaff ? 8 : 7);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Shipments</h2>
          <p className="text-muted-foreground">{data.total} shipment{data.total === 1 ? "" : "s"}</p>
        </div>
        {isStaff && clients.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && selectedIds.length > 0 && (
              <BulkDeleteButton
                selectedIds={selectedIds}
                resource="shipments"
                endpoint="/shipments/bulk-delete"
                testIdRoot="shipments"
                onDeleted={load}
              />
            )}
            <ShipmentForm clients={clients} availableProducts={availableProducts} onSaved={load} />
          </div>
        )}
      </div>
      {isStaff && clients.length === 0 && !loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          To get started, add a client on the{" "}
          <Link to="/clients" className="font-semibold underline hover:text-amber-950">Clients page</Link>
          {" "}— then you can add users under that client and start creating shipments here.
        </div>
      )}
      <SearchFilters statuses={SHIPMENT_STATUSES} clients={isStaff ? clients : null} placeholder="Search shipment, tracking, store..." />
      <Card>
        <CardContent className="p-0">
          {loading ? <TableSkeleton rows={6} cols={colCount} /> :
            data.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      {isAdmin && (
                        <th className="w-10 px-4 py-3">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Select all shipments on this page"
                            data-testid="shipments-select-all"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 font-medium">Shipment</th>
                      {isStaff && <th className="px-4 py-3 font-medium">Organization</th>}
                      <th className="px-4 py-3 font-medium">Product</th>
                      <th className="px-4 py-3 font-medium">Crate</th>
                      <th className="px-4 py-3 font-medium">Destination</th>
                      <th className="px-4 py-3 font-medium">Carrier</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((s) => (
                      <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedIds.includes(s.id)}
                              onCheckedChange={() => toggleOne(s.id)}
                              aria-label={`Select shipment ${s.shipment_code}`}
                              data-testid={`shipments-row-select-${s.id}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3"><Link to={`/shipments/${s.id}`} className="font-medium text-primary hover:underline">{s.shipment_code}</Link></td>
                        {isStaff && <td className="px-4 py-3">{s.client_name || "—"}</td>}
                        <td className="px-4 py-3">{s.product?.serial_number || "—"}</td>
                        <td className="px-4 py-3">{s.crate?.crate_code || "—"}</td>
                        <td className="px-4 py-3">{s.store_name} · {s.city}, {s.state}</td>
                        <td className="px-4 py-3">{s.carrier || "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <Truck className="h-5 w-5" />
                <p>No shipments match. Create one to get started.</p>
              </div>
            )
          }
        </CardContent>
      </Card>
      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} />
    </div>
  );
}
